import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TokenService } from "@/gen/omnios/v1/token_connect";
import { transport } from "@/lib/grpc";
import { cn } from "@/lib/utils";
import { createClient } from "@connectrpc/connect";
import { createFileRoute } from "@tanstack/react-router";
import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";

export const Route = createFileRoute("/docs/portal/endpoints")({
	component: EndpointsPage,
});

/* ── Global API key context ───────────────────────── */

const ApiKeyCtx = createContext<{
	apiKey: string;
	setApiKey: (k: string) => void;
	baseUrl: string;
}>({ apiKey: "", setApiKey: () => {}, baseUrl: "" });

function useApiKey() {
	return useContext(ApiKeyCtx);
}

/* ── Method badge ─────────────────────────────────── */

function M({ m }: { m: string }) {
	const c: Record<string, string> = {
		GET: "text-emerald-600 dark:text-emerald-400",
		POST: "text-blue-600 dark:text-blue-400",
		PATCH: "text-amber-600 dark:text-amber-400",
		DELETE: "text-red-600 dark:text-red-400",
	};
	return (
		<span
			className={cn("text-[11px] font-bold font-mono", c[m] ?? "text-zinc-500")}
		>
			{m}
		</span>
	);
}

/* ── Try-It ───────────────────────────────────────── */

function TryIt({
	method,
	path,
	defaultParams,
	defaultBody,
}: {
	method: string;
	path: string;
	defaultParams?: Record<string, string>;
	defaultBody?: string;
}) {
	const { apiKey, baseUrl } = useApiKey();
	const [params, setParams] = useState<Record<string, string>>(
		defaultParams ?? {},
	);
	const [body, setBody] = useState(defaultBody ?? "");
	const [result, setResult] = useState<string | null>(null);
	const [status, setStatus] = useState<number | null>(null);
	const [loading, setLoading] = useState(false);
	const [open, setOpen] = useState(false);

	const pathParams = useMemo(() => {
		const x = path.match(/:(\w+)/g);
		return x?.map((s) => s.slice(1)) ?? [];
	}, [path]);

	const resolvedPath = useMemo(() => {
		let p = path;
		for (const k of pathParams) p = p.replace(`:${k}`, params[k] || `:${k}`);
		return p;
	}, [path, pathParams, params]);

	const qs = useMemo(() => {
		const q = Object.entries(params).filter(
			([k, v]) => v && !pathParams.includes(k),
		);
		return q.length
			? "?" + q.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&")
			: "";
	}, [params, pathParams]);

	const run = useCallback(async () => {
		if (!apiKey) return;
		setLoading(true);
		setResult(null);
		setStatus(null);
		try {
			const url = `${baseUrl}${resolvedPath}${qs}`;
			const opts: RequestInit = {
				method,
				headers: { "X-API-Key": apiKey, "Content-Type": "application/json" },
			};
			if (body && method !== "GET" && method !== "DELETE") opts.body = body;
			const r = await fetch(url, opts);
			setStatus(r.status);
			const t = await r.text();
			try {
				setResult(JSON.stringify(JSON.parse(t), null, 2));
			} catch {
				setResult(t);
			}
		} catch (e: any) {
			setResult(`Error: ${e.message}`);
		} finally {
			setLoading(false);
		}
	}, [apiKey, baseUrl, resolvedPath, qs, method, body]);

	if (!open) {
		return (
			<Button
				type="button"
				variant="ghost"
				onClick={() => setOpen(true)}
				className="mt-1 text-[11px] text-zinc-400 hover:text-emerald-500 h-auto p-0 flex items-center gap-1"
			>
				<span className="text-emerald-500 text-[9px]">▶</span> Попробовать
			</Button>
		);
	}

	return (
		<div className="mt-2 rounded bg-[#0a0a0a] border border-zinc-800 overflow-hidden">
			<div className="px-3 py-2.5 space-y-2">
				{Object.keys(defaultParams ?? {}).length > 0 && (
					<div className="flex flex-wrap gap-2">
						{Object.entries(defaultParams ?? {}).map(([k, ph]) => (
							<div key={k} className="flex items-center gap-1">
								<Label className="text-[10px] text-zinc-600 font-mono">
									{k}
								</Label>
								<Input
									value={params[k] ?? ""}
									onChange={(e) =>
										setParams((p) => ({ ...p, [k]: e.target.value }))
									}
									placeholder={ph}
									className="bg-zinc-900 border-zinc-800 rounded px-2 py-0.5 text-[11px] text-zinc-200 font-mono w-32 focus:border-zinc-600 placeholder:text-zinc-700 h-auto shadow-none"
								/>
							</div>
						))}
					</div>
				)}
				{defaultBody && (
					<Textarea
						value={body}
						onChange={(e) => setBody(e.target.value)}
						rows={Math.min(body.split("\n").length + 1, 8)}
						className="w-full bg-zinc-900 border-zinc-800 rounded px-2 py-1.5 text-[11px] text-zinc-200 font-mono focus:border-zinc-600 resize-y shadow-none"
					/>
				)}
				<div className="flex items-center gap-2">
					<code className="flex-1 text-[10px] text-zinc-600 font-mono truncate">
						{method} {resolvedPath}
						{qs}
					</code>
					<Button
						type="button"
						onClick={run}
						disabled={!apiKey || loading}
						className="px-3 py-0.5 rounded text-[11px] font-semibold bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-30 h-auto"
					>
						{loading ? "···" : "Send"}
					</Button>
					<Button
						type="button"
						variant="ghost"
						onClick={() => setOpen(false)}
						className="text-zinc-600 hover:text-zinc-400 text-[10px] h-auto p-0"
					>
						✕
					</Button>
				</div>
				{result !== null && (
					<div className="border-t border-zinc-800 pt-2 -mx-3 px-3">
						<div className="flex items-center gap-1.5 mb-1">
							<span className="text-[9px] text-zinc-600 font-mono uppercase">
								Response
							</span>
							{status && (
								<span
									className={cn(
										"text-[10px] font-bold font-mono",
										status < 300
											? "text-emerald-500"
											: status < 500
												? "text-amber-500"
												: "text-red-500",
									)}
								>
									{status}
								</span>
							)}
						</div>
						<pre className="text-[11px] text-zinc-300 font-mono overflow-x-auto max-h-60 overflow-y-auto leading-relaxed">
							{result}
						</pre>
					</div>
				)}
			</div>
		</div>
	);
}

/* ── Doc building blocks ──────────────────────────── */

function Ep({
	method,
	path,
	children,
	scope,
	tryParams,
	tryBody,
	id,
}: {
	method: string;
	path: string;
	children: React.ReactNode;
	scope?: string;
	tryParams?: Record<string, string>;
	tryBody?: string;
	id?: string;
}) {
	return (
		<section id={id} className="mb-8 scroll-mt-20">
			<div className="flex items-baseline gap-2 mb-2">
				<M m={method} />
				<code className="text-[13px] font-semibold text-zinc-900 dark:text-zinc-100 font-mono">
					{path}
				</code>
				{scope && (
					<span className="text-[9px] font-mono text-zinc-400 ml-auto">
						{scope}
					</span>
				)}
			</div>
			<div className="space-y-2 text-[13px] text-zinc-500 dark:text-zinc-400 leading-relaxed">
				{children}
				<TryIt
					method={method}
					path={path}
					defaultParams={tryParams}
					defaultBody={tryBody}
				/>
			</div>
		</section>
	);
}

function Params({
	params,
}: {
	params: { name: string; type: string; required?: boolean; desc: string }[];
}) {
	return (
		<div className="text-[12px]">
			<Table>
				<TableHeader>
					<TableRow className="border-b border-zinc-200 dark:border-zinc-800">
						<TableHead className="text-left py-1 pr-3 text-[10px] text-zinc-400 uppercase tracking-wider font-medium h-auto">
							Параметр
						</TableHead>
						<TableHead className="text-left py-1 pr-3 text-[10px] text-zinc-400 uppercase tracking-wider font-medium h-auto">
							Тип
						</TableHead>
						<TableHead className="text-left py-1 text-[10px] text-zinc-400 uppercase tracking-wider font-medium h-auto">
							Описание
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{params.map((p) => (
						<TableRow
							key={p.name}
							className="border-b border-zinc-100 dark:border-zinc-900/50 last:border-0"
						>
							<TableCell className="py-1.5 pr-3 whitespace-nowrap">
								<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
									{p.name}
								</code>
								{p.required && (
									<span className="text-red-500 text-[9px] ml-0.5">*</span>
								)}
							</TableCell>
							<TableCell className="py-1.5 pr-3">
								<span className="text-[10px] text-zinc-400 font-mono">
									{p.type}
								</span>
							</TableCell>
							<TableCell className="py-1.5 text-zinc-500">{p.desc}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}

function Pre({ title, children }: { title?: string; children: string }) {
	return (
		<div className="rounded bg-[#0a0a0a] border border-zinc-800 overflow-hidden">
			{title && (
				<div className="px-3 py-1 text-[9px] text-zinc-500 uppercase tracking-widest border-b border-zinc-800 font-medium">
					{title}
				</div>
			)}
			<pre className="px-3 py-2 overflow-x-auto text-[11px] leading-relaxed text-zinc-300 font-mono">
				{children}
			</pre>
		</div>
	);
}

function H({ id, children }: { id: string; children: React.ReactNode }) {
	return (
		<h2
			id={id}
			className="text-base font-semibold text-zinc-900 dark:text-zinc-100 mt-14 mb-4 pt-6 border-t border-zinc-100 dark:border-zinc-800/50 scroll-mt-20"
		>
			{children}
		</h2>
	);
}

/* ── Endpoint registry for search ─────────────────── */

const ENDPOINTS = [
	{
		id: "ep-search",
		method: "GET",
		path: "/api/v1/portal/search",
		section: "search",
		label: "Поиск продуктов",
	},
	{
		id: "ep-list",
		method: "GET",
		path: "/api/v1/portal/products",
		section: "products",
		label: "Список продуктов",
	},
	{
		id: "ep-create",
		method: "POST",
		path: "/api/v1/portal/products",
		section: "products",
		label: "Создание продукта",
	},
	{
		id: "ep-update",
		method: "PATCH",
		path: "/api/v1/portal/products/:id",
		section: "products",
		label: "Обновление продукта",
	},
	{
		id: "ep-delete",
		method: "DELETE",
		path: "/api/v1/portal/products/:id",
		section: "products",
		label: "Удаление продукта",
	},
	{
		id: "ep-catalog",
		method: "GET",
		path: "/api/v1/portal/catalog",
		section: "catalog",
		label: "B2B-каталог клиента",
	},
	{
		id: "ep-props",
		method: "GET",
		path: "/api/v1/portal/property-definitions",
		section: "schema",
		label: "Определения свойств",
	},
	{
		id: "ep-cats",
		method: "GET",
		path: "/api/v1/portal/categories",
		section: "schema",
		label: "Дерево категорий",
	},
	{
		id: "ep-parties",
		method: "GET",
		path: "/api/v1/portal/parties",
		section: "parties",
		label: "Список контрагентов",
	},
	{
		id: "ep-party",
		method: "GET",
		path: "/api/v1/portal/parties/:id",
		section: "parties",
		label: "Контрагент по ID",
	},
	{
		id: "ep-mint",
		method: "POST",
		path: "/api/v1/portal/keys",
		section: "keys",
		label: "Создание ключа",
	},
	{
		id: "ep-suggest",
		method: "GET",
		path: "/api/v1/portal/suggest",
		section: "suggest",
		label: "Автодополнение",
	},
	{
		id: "ep-detail",
		method: "GET",
		path: "/api/v1/portal/products/:id",
		section: "detail",
		label: "Детали продукта",
	},
];

/* ── Key panel ────────────────────────────────────── */

function KeyPanel() {
	const { apiKey, setApiKey } = useApiKey();
	const [minting, setMinting] = useState(false);
	const [msg, setMsg] = useState<string | null>(null);
	const tokenClient = useMemo(() => createClient(TokenService, transport), []);

	const mint = useCallback(async () => {
		setMinting(true);
		setMsg(null);
		try {
			const res = await tokenClient.createSearchKey({
				name: "Docs test key",
				expiresInDays: 1,
				scopes: [
					"products:read",
					"products:write",
					"categories:read",
					"properties:read",
					"keys:mint",
				],
			});
			setApiKey(res.plaintext);
			setMsg("Ключ создан · 24ч");
		} catch (e: any) {
			setMsg(`Ошибка: ${e.message}`);
		} finally {
			setMinting(false);
		}
	}, [tokenClient, setApiKey]);

	return (
		<div className="flex items-center gap-2 mb-8 p-3 rounded bg-[#0a0a0a] border border-zinc-800">
			<span className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider shrink-0">
				Key
			</span>
			<Input
				value={apiKey}
				onChange={(e) => setApiKey(e.target.value)}
				placeholder="omnios_pk_..."
				className="flex-1 min-w-0 bg-transparent text-xs text-zinc-200 font-mono placeholder:text-zinc-700 border-0 shadow-none focus-visible:ring-0 h-auto p-0"
			/>
			<Button
				type="button"
				variant="outline"
				onClick={mint}
				disabled={minting}
				className="shrink-0 px-3 py-1 rounded text-[11px] font-medium bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white disabled:opacity-40 border-zinc-700 h-auto"
			>
				{minting ? "···" : "Создать"}
			</Button>
			{msg && (
				<span
					className={cn(
						"text-[10px] font-mono shrink-0",
						msg.startsWith("Ошибка") ? "text-red-400" : "text-emerald-500",
					)}
				>
					{msg}
				</span>
			)}
		</div>
	);
}

/* ── Search bar ───────────────────────────────────── */

function DocSearch() {
	const [q, setQ] = useState("");
	const [focused, setFocused] = useState(false);

	const results = useMemo(() => {
		if (!q || q.length < 2) return [];
		const lq = q.toLowerCase();
		return ENDPOINTS.filter(
			(e) =>
				e.path.toLowerCase().includes(lq) ||
				e.label.toLowerCase().includes(lq) ||
				e.method.toLowerCase().includes(lq),
		);
	}, [q]);

	return (
		<div className="relative mb-6">
			<Input
				value={q}
				onChange={(e) => setQ(e.target.value)}
				onFocus={() => setFocused(true)}
				onBlur={() => setTimeout(() => setFocused(false), 150)}
				placeholder="Поиск эндпоинтов…"
				className="w-full bg-zinc-50 dark:bg-zinc-900/50 border-zinc-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-xs text-zinc-700 dark:text-zinc-300 placeholder:text-zinc-400 focus:border-zinc-400 dark:focus:border-zinc-600"
			/>
			{focused && results.length > 0 && (
				<div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg shadow-lg z-50 overflow-hidden">
					{results.map((r) => (
						<a
							key={r.id}
							href={"#${r.id}"}
							className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
							onClick={() => {
								setQ("");
								setFocused(false);
							}}
						>
							<M m={r.method} />
							<code className="text-[11px] font-mono text-zinc-600 dark:text-zinc-400">
								{r.path}
							</code>
							<span className="text-[11px] text-zinc-400 ml-auto">
								{r.label}
							</span>
						</a>
					))}
				</div>
			)}
		</div>
	);
}

/* ── Live usage panel ─────────────────────────────── */

function UsagePanel() {
	const tokenClient = useMemo(() => createClient(TokenService, transport), []);
	const [data, setData] = useState<{
		current: number;
		included: number;
		plan: string;
	} | null>(null);
	const [err, setErr] = useState(false);

	useState(() => {
		tokenClient
			.getSearchKeyUsage({})
			.then((r) =>
				setData({
					current: Number(r.currentMonthQueries),
					included: Number(r.includedQueries),
					plan: r.planSlug,
				}),
			)
			.catch(() => setErr(true));
	});

	if (err || !data) return null;

	const pct =
		data.included > 0
			? Math.min(100, Math.round((data.current / data.included) * 100))
			: 0;

	return (
		<div className="rounded border border-zinc-200 dark:border-zinc-800 p-4 mb-4">
			<div className="flex items-baseline gap-3 mb-2">
				<span className="text-[12px] font-semibold text-zinc-700 dark:text-zinc-300">
					Использование в этом месяце
				</span>
				<span className="text-[10px] text-zinc-400 font-mono uppercase">
					{data.plan}
				</span>
			</div>
			<div className="flex items-center gap-3">
				<div className="flex-1 h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
					<div
						className={cn(
							"h-full rounded-full transition-all",
							pct > 90
								? "bg-red-500"
								: pct > 70
									? "bg-amber-500"
									: "bg-emerald-500",
						)}
						style={{ width: `${pct}%` }}
					/>
				</div>
				<span className="text-[11px] font-mono text-zinc-500 shrink-0">
					{data.current.toLocaleString()} / {data.included.toLocaleString()}
				</span>
			</div>
		</div>
	);
}

/* ── Page ─────────────────────────────────────────── */

function EndpointsPage() {
	const [apiKey, setApiKey] = useState("");

	const baseUrl =
		import.meta.env.VITE_PORTAL_API_URL ?? "http://localhost:50101";

	return (
		<ApiKeyCtx.Provider value={{ apiKey, setApiKey, baseUrl }}>
			<div className="w-full">
				{/* Header */}
				<div className="flex items-baseline gap-3 mb-1">
					<h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
						Portal API
					</h1>
					<code className="text-[11px] font-mono text-zinc-400">{baseUrl}</code>
				</div>
				<p className="text-[13px] text-zinc-400 mb-5">
					Управление продуктами контрагентов, B2B-каталоги, поиск, схема данных.
				</p>

				<DocSearch />
				<KeyPanel />

				{/* ── Quickstart ──────────────────── */}
				<H id="quickstart">Быстрый старт</H>

				<p className="text-[13px] text-zinc-500 mb-4 leading-relaxed">
					Создайте API-ключ в настройках витрины и выполните первый запрос:
				</p>

				<Pre title="1. Поиск продуктов">{`curl ${baseUrl}/api/v1/portal/search?q=подшипник \\
  -H "X-API-Key: omnios_pk_ваш_ключ"`}</Pre>

				<div className="mt-3" />
				<Pre title="2. Получение продукта по ID">{`curl ${baseUrl}/api/v1/portal/products/550e8400-e29b-41d4-a716-446655440000 \\
  -H "X-API-Key: omnios_pk_ваш_ключ"`}</Pre>

				<div className="mt-3" />
				<Pre title="3. Автодополнение">{`curl "${baseUrl}/api/v1/portal/suggest?q=под&limit=5" \\
  -H "X-API-Key: omnios_pk_ваш_ключ"`}</Pre>

				<p className="text-[13px] text-zinc-500 mt-4 mb-4 leading-relaxed">
					Этого достаточно для базовой интеграции — поиск, просмотр товаров и
					автодополнение. Ключ по умолчанию имеет права{" "}
					<code className="text-[11px]">products:read</code>,{" "}
					<code className="text-[11px]">categories:read</code>,{" "}
					<code className="text-[11px]">properties:read</code> и работает со
					всем вашим каталогом.
				</p>

				<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
					Для продвинутых сценариев API поддерживает два режима работы с
					контрагентами:
				</p>

				<div className="mb-4 text-[13px] text-zinc-500 leading-relaxed space-y-3">
					<div>
						<span className="font-semibold text-zinc-800 dark:text-zinc-200">
							Каталог контрагента
						</span>{" "}
						— ведение собственного каталога продуктов контрагента (клиента,
						поставщика, конкурента) через тот же PIM. Продукты привязаны через{" "}
						<code className="text-[11px]">owner_party_id</code> и управляются
						через{" "}
						<code className="text-[11px]">POST/PATCH/DELETE /products</code>.
					</div>
					<div>
						<span className="font-semibold text-zinc-800 dark:text-zinc-200">
							B2B-каталог
						</span>{" "}
						— персональный каталог для клиента: назначьте ему продукты из вашего
						каталога с индивидуальными ценами. Клиент получает свой каталог
						через <code className="text-[11px]">GET /catalog</code>.
					</div>
				</div>

				<div className="mb-6 rounded bg-zinc-50 dark:bg-zinc-900/30 border border-zinc-200 dark:border-zinc-800 px-4 py-3">
					<div className="text-[12px] text-zinc-500 leading-relaxed">
						<span className="font-semibold text-zinc-700 dark:text-zinc-300">
							Оба сценария
						</span>{" "}
						работают через ключ контрагента. Создайте мастер-ключ с{" "}
						<code className="text-[10px]">keys:mint</code> →{" "}
						<code className="text-[10px]">POST /keys</code> с party_id → клиент
						использует свой ключ: <code className="text-[10px]">/products</code>{" "}
						для своего каталога, <code className="text-[10px]">/catalog</code>{" "}
						для назначенных ему товаров.
					</div>
				</div>

				{/* ── Auth ─────────────────────────── */}
				<H id="auth">Аутентификация</H>

				<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
					Все запросы требуют заголовок{" "}
					<code className="text-[11px] font-semibold">X-API-Key</code>.
				</p>

				<Pre title="Заголовок">
					{"X-API-Key: omnios_pk_AbCdEfGhIjKlMnOp..."}
				</Pre>

				<p className="text-[13px] text-zinc-500 mt-4 mb-3 leading-relaxed">
					Два типа ключей:
					<strong className="text-zinc-700 dark:text-zinc-300">
						{" "}
						мастер-ключ
					</strong>{" "}
					— workspace-level, видит все продукты, может создавать дочерние ключи.{" "}
					<strong className="text-zinc-700 dark:text-zinc-300">
						Ключ контрагента
					</strong>{" "}
					— привязан к <code className="text-[11px]">party_id</code>, все
					запросы автоматически фильтруются по его продуктам.
				</p>

				<h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mt-6 mb-2">
					Scopes
				</h3>

				<div className="rounded border border-zinc-200 dark:border-zinc-800 mb-4 text-[12px]">
					<Table>
						<TableHeader>
							<TableRow className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50">
								<TableHead className="text-left py-1.5 px-3 text-[10px] text-zinc-400 uppercase tracking-wider font-medium h-auto">
									Scope
								</TableHead>
								<TableHead className="text-left py-1.5 px-3 text-[10px] text-zinc-400 uppercase tracking-wider font-medium h-auto">
									Описание
								</TableHead>
								<TableHead className="text-left py-1.5 px-3 text-[10px] text-zinc-400 uppercase tracking-wider font-medium w-12 h-auto">
									Умолч
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody className="divide-y divide-zinc-100 dark:divide-zinc-800/50">
							{(
								[
									["products:read", "Поиск, список, детали продуктов", "✓"],
									[
										"products:write",
										"Создание, обновление, удаление продуктов",
										"—",
									],
									["categories:read", "Дерево категорий", "✓"],
									["properties:read", "Определения свойств", "✓"],
									["parties:read", "Список и детали контрагентов", "✓"],
									[
										"keys:mint",
										"Создание дочерних ключей для контрагентов",
										"—",
									],
								] as const
							).map(([s, d, def]) => (
								<TableRow key={s}>
									<TableCell className="py-1 px-3">
										<code className="text-[11px] font-mono font-semibold">
											{s}
										</code>
									</TableCell>
									<TableCell className="py-1 px-3 text-zinc-500">{d}</TableCell>
									<TableCell className="py-1 px-3 text-zinc-400 text-center">{def}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>

				{/* ── Search ───────────────────────── */}
				<H id="search">Поиск</H>

				<Ep
					id="ep-search"
					method="GET"
					path="/api/v1/portal/search"
					scope="products:read"
					tryParams={{ q: "подшипник", per_page: "5", owner_party_id: "" }}
				>
					<p>
						Полнотекстовый поиск через Typesense. Для ключей контрагента
						автоматически фильтрует по owner_party_id.
					</p>
					<Params
						params={[
							{ name: "q", type: "string", desc: "Поисковый запрос" },
							{ name: "page", type: "int", desc: "Страница (по умолчанию: 1)" },
							{
								name: "per_page",
								type: "int",
								desc: "Результатов (1–100, по умолчанию: 20)",
							},
							{
								name: "category_id",
								type: "UUID",
								desc: "Фильтр по категории",
							},
							{ name: "brand", type: "string", desc: "Фильтр по бренду" },
							{ name: "min_price", type: "int", desc: "Мин. цена (копейки)" },
							{ name: "max_price", type: "int", desc: "Макс. цена (копейки)" },
							{
								name: "sort",
								type: "string",
								desc: "price_asc | price_desc | newest | title_asc | title_desc",
							},
							{
								name: "owner_party_id",
								type: "UUID",
								desc: "Фильтр по владельцу",
							},
						]}
					/>
					<Pre title="Response">{`{"products": [{"id": "...", "title": "Подшипник 6205-2RS", "brand": "SKF",
  "price": {"amount_cents": 45000, "currency": "RUB"}, "relevance_score": 0.95}],
 "total": 142, "page": 1, "per_page": 20}`}</Pre>
				</Ep>

				{/* ── Products CRUD ────────────────── */}
				<H id="products">CRUD продуктов</H>

				<Ep
					id="ep-list"
					method="GET"
					path="/api/v1/portal/products"
					scope="products:read"
					tryParams={{ owner_party_id: "", search: "", page_size: "20" }}
				>
					<p>
						Список продуктов с пагинацией. Для привязанных ключей — автофильтр
						по контрагенту.
					</p>
					<Params
						params={[
							{
								name: "owner_party_id",
								type: "UUID",
								desc: "Контрагент-владелец (авто для привязанных ключей)",
							},
							{ name: "search", type: "string", desc: "Поиск по названию" },
							{
								name: "category_id",
								type: "UUID",
								desc: "Фильтр по категории",
							},
							{
								name: "page_size",
								type: "int",
								desc: "Размер страницы (1–200)",
							},
							{
								name: "page_token",
								type: "string",
								desc: "Токен следующей страницы",
							},
						]}
					/>
					<Pre title="Response">{`{"items": [{"id": "...", "title": "Подшипник 6205-2RS",
  "pricing": [{"name": "default", "value_cents": 45000, "currency": "RUB"}],
  "vendor_codes": ["6205-2RS"], "owner_party_id": "..."}],
 "next_page_token": "50"}`}</Pre>
				</Ep>

				<Ep
					id="ep-create"
					method="POST"
					path="/api/v1/portal/products"
					scope="products:write"
					tryBody={JSON.stringify(
						{
							title: "Новый продукт",
							pricing: [
								{ name: "default", amount_cents: 10000, currency: "RUB" },
							],
							vendor_codes: ["TEST-001"],
						},
						null,
						2,
					)}
				>
					<p>
						Создание продукта. Для привязанных ключей owner_party_id
						устанавливается автоматически.
					</p>
					<Params
						params={[
							{
								name: "title",
								type: "string",
								required: true,
								desc: "Название",
							},
							{ name: "description", type: "string", desc: "Описание" },
							{
								name: "owner_party_id",
								type: "UUID",
								desc: "Контрагент-владелец",
							},
							{
								name: "pricing",
								type: "array",
								desc: "[{name, amount_cents, currency}]",
							},
							{ name: "properties", type: "object", desc: "Свойства продукта" },
							{ name: "vendor_codes", type: "string[]", desc: "Артикулы" },
							{ name: "category_ids", type: "UUID[]", desc: "Категории" },
							{ name: "is_draft", type: "bool", desc: "Черновик" },
						]}
					/>
				</Ep>

				<Ep
					id="ep-update"
					method="PATCH"
					path="/api/v1/portal/products/:id"
					scope="products:write"
					tryParams={{ id: "" }}
					tryBody={JSON.stringify({ title: "Обновлённое название" }, null, 2)}
				>
					<p>
						Частичное обновление. Только изменяемые поля. Для привязанных ключей
						— продукт должен принадлежать контрагенту.
					</p>
				</Ep>

				<Ep
					id="ep-delete"
					method="DELETE"
					path="/api/v1/portal/products/:id"
					scope="products:write"
					tryParams={{ id: "" }}
				>
					<p>Мягкое удаление продукта.</p>
				</Ep>

				{/* ── Catalog ──────────────────────── */}
				<H id="catalog">B2B-каталог</H>

				<Ep
					id="ep-catalog"
					method="GET"
					path="/api/v1/portal/catalog"
					scope="products:read"
					tryParams={{ search: "", page_size: "20" }}
				>
					<p>
						Продукты из вашего каталога, назначенные контрагенту. Индивидуальные
						цены подставляются автоматически. Требует ключ с привязкой к
						контрагенту.
					</p>
					<Params
						params={[
							{ name: "search", type: "string", desc: "Поиск по названию" },
							{
								name: "category_id",
								type: "UUID",
								desc: "Фильтр по категории",
							},
							{
								name: "page_size",
								type: "int",
								desc: "Размер страницы (1–200)",
							},
							{
								name: "page_token",
								type: "string",
								desc: "Токен следующей страницы",
							},
						]}
					/>
					<Pre title="Response">{`{"items": [{"id": "...", "title": "Подшипник 6205-2RS",
  "pricing": [{"name": "default", "value_cents": 38000, "currency": "RUB"}],
  "vendor_codes": ["6205-2RS"]}],
 "next_page_token": ""}`}</Pre>
				</Ep>

				{/* ── Schema ───────────────────────── */}
				<H id="schema">Схема данных</H>

				<Ep
					id="ep-props"
					method="GET"
					path="/api/v1/portal/property-definitions"
					scope="properties:read"
				>
					<p>
						Определения свойств: типы, единицы, допустимые значения, группы.
					</p>
					<Pre title="Response">{`{"items": [
  {"id": "...", "name": "Бренд", "slug": "brand", "value_type": "string", "is_filterable": true, "allowed_values": ["SKF", "FAG"]},
  {"id": "...", "name": "Масса", "slug": "weight", "value_type": "number", "unit": "г"}
]}`}</Pre>
				</Ep>

				<Ep
					id="ep-cats"
					method="GET"
					path="/api/v1/portal/categories"
					scope="categories:read"
				>
					<p>Дерево категорий продуктов.</p>
					<Pre title="Response">{`{"items": [
  {"id": "...", "parent_id": null, "name": "Подшипники", "slug": "podshipniki", "path": "podshipniki", "children_count": 5},
  {"id": "...", "parent_id": "...", "name": "Шариковые", "path": "podshipniki/sharikovye", "children_count": 0}
]}`}</Pre>
				</Ep>

				{/* ── Parties ─────────────────────── */}
				<H id="parties">Контрагенты</H>

				<Ep
					id="ep-parties"
					method="GET"
					path="/api/v1/portal/parties"
					scope="parties:read"
					tryParams={{ kind: "", role: "", search: "", page_size: "20" }}
				>
					<p>
						Список контрагентов workspace с фильтрацией по типу, роли и
						текстовому поиску.
					</p>
					<Params
						params={[
							{
								name: "kind",
								type: "string",
								desc: "Тип: person, organization, team, agent",
							},
							{
								name: "role",
								type: "string",
								desc: "Роль: customer, supplier, partner и т.д.",
							},
							{
								name: "search",
								type: "string",
								desc: "Поиск по имени (fuzzy)",
							},
							{
								name: "page_size",
								type: "int",
								desc: "Размер страницы (1–200, по умолчанию: 20)",
							},
							{
								name: "page_token",
								type: "string",
								desc: "Токен следующей страницы",
							},
						]}
					/>
					<Pre title="Response">{`{"items": [{"id": "...", "kind": "organization", "name": "ООО Промснаб",
  "display_name": "Промснаб", "is_internal": false,
  "roles": ["customer", "supplier"], "attrs": {"inn": "7707083893"}}],
 "total": 42, "next_page_token": "20"}`}</Pre>
				</Ep>

				<Ep
					id="ep-party"
					method="GET"
					path="/api/v1/portal/parties/:id"
					scope="parties:read"
					tryParams={{ id: "" }}
				>
					<p>
						Полная информация о контрагенте: роли, каналы связи, условия оплаты.
					</p>
					<Pre title="Response">{`{"party": {"id": "...", "kind": "organization", "name": "ООО Промснаб",
  "display_name": "Промснаб", "is_internal": false,
  "pricing_tier": "key", "payment_terms_days": 30, "relationship_score": 0.85,
  "roles": ["customer"], "channels": [{"kind": "email", "scope": "work", "identifier": "info@promsnab.ru"}],
  "attrs": {"inn": "7707083893"}}}`}</Pre>
				</Ep>

				{/* ── Keys ─────────────────────────── */}
				<H id="keys">Ключи API</H>

				<Ep
					id="ep-mint"
					method="POST"
					path="/api/v1/portal/keys"
					scope="keys:mint"
					tryBody={JSON.stringify(
						{
							name: "Тестовый ключ",
							party_id: "",
							scopes: ["products:read"],
							expires_in_days: 30,
						},
						null,
						2,
					)}
				>
					<p>
						Создание дочернего ключа с привязкой к контрагенту. Права —
						подмножество мастер-ключа.{" "}
						<code className="text-[11px]">keys:mint</code> нельзя делегировать.
					</p>
					<Params
						params={[
							{
								name: "name",
								type: "string",
								required: true,
								desc: "Название",
							},
							{
								name: "party_id",
								type: "UUID",
								required: true,
								desc: "ID контрагента",
							},
							{ name: "scopes", type: "string[]", desc: "Права доступа" },
							{
								name: "expires_in_days",
								type: "int",
								desc: "Срок действия (1–365)",
							},
							{
								name: "rate_limit_rpm",
								type: "int",
								desc: "Лимит запросов/мин",
							},
							{
								name: "allowed_origins",
								type: "string[]",
								desc: "CORS-домены",
							},
						]}
					/>
					<Pre title="Response">{`{"key": {"id": "...", "name": "Ключ для ООО Промснаб", "token_prefix": "omnios_pk_AbCd",
  "party_id": "...", "scopes": ["products:read"], "expires_at": "2027-03-23T00:00:00Z"},
 "plaintext": "omnios_pk_AbCdEfGh..."}`}</Pre>
					<p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
						Открытый текст ключа показывается только один раз.
					</p>
				</Ep>

				{/* ── Suggest ───────────────────────── */}
				<H id="suggest">Автодополнение</H>

				<Ep
					id="ep-suggest"
					method="GET"
					path="/api/v1/portal/suggest"
					scope="products:read"
					tryParams={{ q: "под", limit: "5" }}
				>
					<p>Быстрое автодополнение для поисковой строки.</p>
					<Params
						params={[
							{
								name: "q",
								type: "string",
								required: true,
								desc: "Текст (мин. 2 символа)",
							},
							{
								name: "limit",
								type: "int",
								desc: "Макс. результатов (по умолчанию: 5)",
							},
						]}
					/>
				</Ep>

				{/* ── Detail ────────────────────────── */}
				<H id="detail">Детали продукта</H>

				<Ep
					id="ep-detail"
					method="GET"
					path="/api/v1/portal/products/:id"
					scope="products:read"
					tryParams={{ id: "" }}
				>
					<p>Полная информация о продукте: цены, свойства, медиа.</p>
					<Pre title="Response">{`{"product": {"id": "...", "title": "Подшипник 6205-2RS",
  "vendor_codes": ["6205-2RS"],
  "prices": [{"name": "Розница", "amount_cents": 45000, "currency": "RUB", "is_default": true}],
  "properties": {"d": "25 мм", "Бренд": "SKF"},
  "media_ids": ["..."], "quality_score": 85}}`}</Pre>
				</Ep>

				{/* ── Limits ────────────────────────── */}
				<H id="limits">Лимиты и CORS</H>

				<UsagePanel />

				<h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mt-6 mb-2">
					Rate limiting
				</h3>
				<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
					Каждый ключ имеет лимит запросов в минуту (RPM). По умолчанию — 120
					RPM. Можно задать индивидуальный лимит при создании ключа. При
					превышении API возвращает{" "}
					<code className="text-[11px]">429 Too Many Requests</code>.
				</p>

				<h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mt-6 mb-2">
					CORS
				</h3>
				<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
					Каждый ключ может иметь список разрешённых доменов (
					<code className="text-[11px]">allowed_origins</code>). Пустой список =
					доступ с любого домена. Задаётся при создании ключа.
				</p>

				<h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mt-6 mb-2">
					Черновики
				</h3>
				<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
					Продукты с <code className="text-[11px]">is_draft: true</code> не
					отображаются в поиске и списках через API. Используйте это для
					подготовки товаров до публикации.
				</p>

				{/* ── Errors ────────────────────────── */}
				<H id="errors">Ошибки</H>

				<div className="rounded border border-zinc-200 dark:border-zinc-800 mb-3 text-[12px]">
					<Table>
						<TableHeader>
							<TableRow className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50">
								<TableHead className="text-left py-1.5 px-3 text-[10px] text-zinc-400 uppercase tracking-wider font-medium w-12 h-auto">
									HTTP
								</TableHead>
								<TableHead className="text-left py-1.5 px-3 text-[10px] text-zinc-400 uppercase tracking-wider font-medium w-28 h-auto">
									code
								</TableHead>
								<TableHead className="text-left py-1.5 px-3 text-[10px] text-zinc-400 uppercase tracking-wider font-medium h-auto">
									Описание
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody className="divide-y divide-zinc-100 dark:divide-zinc-800/50">
							{(
								[
									["400", "bad_request", "Некорректный запрос"],
									["401", "unauthorized", "Невалидный API-ключ"],
									[
										"403",
										"forbidden",
										"Недостаточно прав или продукт не принадлежит контрагенту",
									],
									["404", "not_found", "Ресурс не найден"],
									["429", "rate_limited", "Превышен лимит запросов/мин"],
									["500", "internal", "Внутренняя ошибка"],
								] as const
							).map(([c, code, d]) => (
								<TableRow key={c}>
									<TableCell className="py-1 px-3 font-mono font-bold text-zinc-700 dark:text-zinc-300">
										{c}
									</TableCell>
									<TableCell className="py-1 px-3 font-mono text-zinc-500">{code}</TableCell>
									<TableCell className="py-1 px-3 text-zinc-500">{d}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
				<Pre>{`{"error": "Missing required scope: products:write", "code": "forbidden"}`}</Pre>

				<div className="mt-14 pt-4 border-t border-zinc-100 dark:border-zinc-800/50 text-center">
					<p className="text-[10px] text-zinc-400 tracking-wider uppercase">
						Portal API v1
					</p>
				</div>
			</div>
		</ApiKeyCtx.Provider>
	);
}
